import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  Eye,
  FileText,
  FlaskConical,
  MessageSquare,
  Power,
  RefreshCw,
  Send,
  ShieldCheck,
  Stethoscope,
  UserRound,
} from "lucide-react";
import {
  buildModule1IdempotencyKey,
  endModule1Session,
  getModule1Options,
  getModule1SessionDetail,
  normalizeApiError,
  orderModule1Test,
  revealModule1Condition,
  submitModule1Turn,
} from "../services/module1Sessions";

function normalizeOrderableOptions(data, optionKey) {
  const items = data?.options?.[optionKey];
  if (!Array.isArray(items)) return [];
  return items
    .map((investigation) => ({
      key: String(investigation?.key || "").trim(),
      label: String(investigation?.label || "").trim(),
      category: String(investigation?.category || "").trim(),
    }))
    .filter(
      (investigation) =>
        investigation.key && investigation.label && investigation.category
    );
}

function groupInvestigationOptions(investigations) {
  const groups = new Map();
  investigations.forEach((investigation) => {
    if (!groups.has(investigation.category)) {
      groups.set(investigation.category, []);
    }
    groups.get(investigation.category).push(investigation);
  });
  return Array.from(groups, ([category, items]) => ({ category, items }));
}

export default function Module1SessionPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [mutating, setMutating] = useState(false);
  const [error, setError] = useState(null);
  const [messageInput, setMessageInput] = useState("");
  const [selectedInvestigationKey, setSelectedInvestigationKey] = useState("");
  const [investigationOptions, setInvestigationOptions] = useState([]);
  const [investigationOptionsLoading, setInvestigationOptionsLoading] = useState(true);
  const [investigationOptionsError, setInvestigationOptionsError] = useState(null);
  const [testConsent, setTestConsent] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [confirmReveal, setConfirmReveal] = useState(false);
  const [revealedCondition, setRevealedCondition] = useState(null);
  const [conditionVisible, setConditionVisible] = useState(false);
  const [pendingDoctorMessages, setPendingDoctorMessages] = useState([]);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const turnActionRef = useRef(null);
  const investigationActionRef = useRef(null);
  const endActionRef = useRef(null);
  const revealActionRef = useRef(null);

  const loadDetail = useCallback(async ({ background = false } = {}) => {
    if (background) {
      setRefreshing(true);
    } else {
      setInitialLoading(true);
    }
    setError(null);
    try {
      const data = await getModule1SessionDetail(sessionId);
      setDetail(data);
      setPendingDoctorMessages([]);
    } catch (err) {
      setError(normalizeApiError(err));
    } finally {
      if (background) {
        setRefreshing(false);
      } else {
        setInitialLoading(false);
      }
    }
  }, [sessionId]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const loadInvestigationOptions = useCallback(async () => {
    setInvestigationOptionsLoading(true);
    setInvestigationOptionsError(null);
    try {
      const data = await getModule1Options();
      const orderableOptions = [
        ...normalizeOrderableOptions(data, "examinations"),
        ...normalizeOrderableOptions(data, "investigations"),
      ];
      if (!orderableOptions.length) {
        throw new Error("Investigation catalogue is empty.");
      }
      setInvestigationOptions(orderableOptions);
    } catch {
      setInvestigationOptions([]);
      setSelectedInvestigationKey("");
      setInvestigationOptionsError(
        "Investigation options could not be loaded. Please refresh and try again."
      );
    } finally {
      setInvestigationOptionsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInvestigationOptions();
  }, [loadInvestigationOptions]);

  useEffect(() => {
    if (typeof chatEndRef.current?.scrollIntoView === "function") {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [detail, mutating]);

  const messages = useMemo(
    () => [
      ...(detail?.timeline || []).filter((item) => item.type === "message"),
      ...pendingDoctorMessages,
    ],
    [detail, pendingDoctorMessages]
  );

  const activities = useMemo(
    () => (detail?.timeline || []).filter((item) => item.type === "activity"),
    [detail]
  );

  const isActive = detail?.access_mode === "active" && detail?.state === "running";
  const isCompleted = detail?.state === "completed";
  const isAbandoned = detail?.state === "abandoned";
  const isError = detail?.state === "error";
  const isSessionUpdating = mutating || refreshing;
  const selectedInvestigation = useMemo(
    () =>
      investigationOptions.find(
        (investigation) => investigation.key === selectedInvestigationKey
      ),
    [investigationOptions, selectedInvestigationKey]
  );
  const groupedInvestigations = useMemo(
    () => groupInvestigationOptions(investigationOptions),
    [investigationOptions]
  );
  const canOrderInvestigation =
    isActive &&
    !mutating &&
    !investigationOptionsLoading &&
    !investigationOptionsError &&
    Boolean(selectedInvestigation);

  const patientLabel =
    detail?.meta?.patient_name ||
    detail?.meta?.patient_type ||
    detail?.patient_type ||
    "Patient";

  const handleSend = async () => {
    const text = messageInput.trim();
    if (!text || !isActive || mutating) return;
    let action = turnActionRef.current;
    if (action?.inFlight) return;
    if (!isSamePayloadAction(action, sessionId, { userMessage: text })) {
      action = createIdempotencyAction(sessionId, { userMessage: text });
      turnActionRef.current = action;
    }
    action.inFlight = true;
    action.retryable = false;

    setMessageInput("");
    const pendingMessage = {
      type: "message",
      sequence: `pending-${Date.now()}`,
      role: "doctor",
      content: text,
      created_at: new Date().toISOString(),
      pending: true,
    };
    setPendingDoctorMessages((current) => [...current, pendingMessage]);
    setMutating(true);
    setError(null);
    try {
      await submitModule1Turn(sessionId, action.payload.userMessage, {
        idempotencyKey: action.key,
      });
      turnActionRef.current = null;
      try {
        await loadDetail({ background: true });
      } catch (refreshErr) {
        setError(normalizeApiError(refreshErr));
      }
      setTimeout(() => inputRef.current?.focus(), 0);
    } catch (err) {
      const apiError = normalizeApiError(err);
      setPendingDoctorMessages((current) =>
        current.filter((message) => message !== pendingMessage)
      );
      setMessageInput(action.payload.userMessage);
      retainOrRetireAction(turnActionRef, action, apiError);
      setError(apiError);
    } finally {
      if (turnActionRef.current === action) {
        action.inFlight = false;
      }
      setMutating(false);
    }
  };

  const handleOrderTest = async () => {
    const text = selectedInvestigation?.label?.trim();
    if (!text || !canOrderInvestigation) return;
    let action = investigationActionRef.current;
    if (action?.inFlight) return;
    const payload = {
      investigationKey: selectedInvestigation.key,
      testOrder: text,
    };
    if (!isSamePayloadAction(action, sessionId, payload)) {
      action = createIdempotencyAction(sessionId, payload);
      investigationActionRef.current = action;
    }
    action.inFlight = true;
    action.retryable = false;

    setMutating(true);
    setError(null);
    try {
      await orderModule1Test(sessionId, action.payload.testOrder, {
        idempotencyKey: action.key,
      });
      investigationActionRef.current = null;
      setSelectedInvestigationKey("");
      try {
        await loadDetail({ background: true });
      } catch (refreshErr) {
        setError(normalizeApiError(refreshErr));
      }
    } catch (err) {
      const apiError = normalizeApiError(err);
      retainOrRetireAction(investigationActionRef, action, apiError);
      setError(apiError);
    } finally {
      if (investigationActionRef.current === action) {
        action.inFlight = false;
      }
      setMutating(false);
    }
  };

  const handleEndCase = async () => {
    let action = endActionRef.current;
    if (action?.inFlight) return;
    if (!isSamePayloadAction(action, sessionId, {})) {
      action = createIdempotencyAction(sessionId, {});
      endActionRef.current = action;
    }
    action.inFlight = true;
    action.retryable = false;
    setMutating(true);
    setError(null);
    try {
      await endModule1Session(sessionId, { idempotencyKey: action.key });
      endActionRef.current = null;
      setConfirmEnd(false);
      await loadDetail({ background: true });
    } catch (err) {
      const apiError = normalizeApiError(err);
      retainOrRetireAction(endActionRef, action, apiError);
      setError(apiError);
    } finally {
      if (endActionRef.current === action) {
        action.inFlight = false;
      }
      setMutating(false);
    }
  };

  const handleRevealButtonClick = () => {
    if (revealedCondition) {
      setConditionVisible((visible) => !visible);
      return;
    }
    setConfirmReveal(true);
  };

  const handleRevealCondition = async () => {
    let action = revealActionRef.current;
    if (action?.inFlight) return;
    if (!isSamePayloadAction(action, sessionId, {})) {
      action = createIdempotencyAction(sessionId, {});
      revealActionRef.current = action;
    }
    action.inFlight = true;
    action.retryable = false;
    setMutating(true);
    setError(null);
    try {
      const response = await revealModule1Condition(sessionId, {
        idempotencyKey: action.key,
      });
      revealActionRef.current = null;
      setRevealedCondition(response?.condition || "Unknown condition");
      setConditionVisible(true);
      setConfirmReveal(false);
    } catch (err) {
      const apiError = normalizeApiError(err);
      retainOrRetireAction(revealActionRef, action, apiError);
      setError(apiError);
    } finally {
      if (revealActionRef.current === action) {
        action.inFlight = false;
      }
      setMutating(false);
    }
  };

  if (!detail && initialLoading) {
    return (
      <div className="doctor-sim ds-session-page ds-session-loading">
        <div className="ds-workspace-loading">
          <RefreshCw size={18} />
          Restoring session...
        </div>
      </div>
    );
  }

  if (error && !detail) {
    return (
      <div className="doctor-sim ds-session-page ds-session-terminal">
        <button className="btn btn-ghost ds-back-link" onClick={() => navigate("/doctor")}>
          <ArrowLeft size={14} />
          Back to workspace
        </button>
        <SessionErrorState error={error} onRetry={loadDetail} />
      </div>
    );
  }

  const sessionModeClass = isActive ? "ds-session-active" : "ds-session-terminal";

  return (
    <div className={`doctor-sim ds-session-page ${sessionModeClass}`}>
      <div className="ds-session-header">
        <button className="btn btn-ghost ds-back-link" onClick={() => navigate("/doctor")}>
          <ArrowLeft size={14} />
          Back to workspace
        </button>
        <div className="ds-context-bar">
          <div className="ds-context-left">
            <span className="ds-context-badge">
              <Stethoscope size={12} />
              {detail?.meta?.specialty || "Unknown specialty"}
            </span>
            <span className="ds-context-badge ds-context-badge-secondary">
              <UserRound size={12} />
              {detail?.meta?.patient_type || "Unknown persona"}
            </span>
            {isSessionUpdating && (
              <span className="ds-session-sync">
                <RefreshCw size={12} />
                Updating session...
              </span>
            )}
          </div>
          <div className="ds-context-right">
            {isActive && (
              <button
                className="btn btn-secondary ds-reveal-btn"
                onClick={handleRevealButtonClick}
                disabled={mutating}
                title={conditionVisible && revealedCondition ? revealedCondition : undefined}
              >
                <Eye size={14} />
                <span>
                  {conditionVisible && revealedCondition
                    ? revealedCondition
                    : "Reveal Medical Condition"}
                </span>
              </button>
            )}
            {isActive && (
              <button
                className="btn btn-secondary ds-end-btn"
                onClick={() => setConfirmEnd(true)}
                disabled={mutating}
              >
                <Power size={14} />
                End Case and Generate Assessment
              </button>
            )}
          </div>
        </div>
      </div>

      {error && <InlineError error={error} onRetry={() => loadDetail({ background: !!detail })} />}

      {(isCompleted || isAbandoned || isError) && (
        <TerminalNotice
          state={detail.state}
          hasAssessment={!!detail.assessment}
          errorAt={detail.error_at}
        />
      )}

      <div className="ds-main-content">
        <div className="ds-chat-panel">
          <div className="ds-chat-messages">
            {messages.length === 0 ? (
              <div className="ds-session-empty">
                <MessageSquare size={18} />
                No transcript messages are available for this session.
              </div>
            ) : (
              messages.map((message) => (
                <TranscriptMessage
                  key={`${message.sequence}-${message.created_at}`}
                  message={message}
                  patientLabel={patientLabel}
                />
              ))
            )}

            <div ref={chatEndRef} />
          </div>

          <div className="ds-chat-input-bar">
            {isActive ? (
              <div className="ds-input-wrapper">
                <textarea
                  ref={inputRef}
                  value={messageInput}
                  onChange={(event) => setMessageInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault();
                      handleSend();
                    }
                  }}
                  placeholder="Type your question to the patient..."
                  disabled={mutating}
                  className="ds-chat-input"
                  rows={1}
                />
                <button
                  className="ds-send-btn"
                  onClick={handleSend}
                  disabled={mutating || !messageInput.trim()}
                  aria-label="Send message"
                >
                  <Send size={16} />
                </button>
              </div>
            ) : (
              <div className="ds-readonly-input">
                This session is read-only.
              </div>
            )}
          </div>
        </div>

        <div className={`ds-labs-panel ${!testConsent && isActive ? "ds-labs-locked" : ""}`}>
          <div className="ds-labs-header">
            <FlaskConical size={16} />
            <span>Labs & Investigations</span>
          </div>

          {!isActive ? (
            <ActivityList activities={activities} />
          ) : !testConsent ? (
            <div className="ds-labs-consent">
              <div className="ds-consent-icon">
                <ShieldCheck size={24} />
              </div>
              <p className="ds-consent-text">
                Have you notified the patient of your intention for a medical test?
              </p>
              <button
                className="btn btn-primary ds-consent-btn"
                onClick={() => setTestConsent(true)}
              >
                <ShieldCheck size={14} />
                Yes, proceed
              </button>
            </div>
          ) : (
            <div className="ds-labs-content">
              <div className="ds-test-order-form">
                <label htmlFor="ds-investigation-select">Order Investigations</label>
                <div className="ds-test-input-row">
                  <select
                    id="ds-investigation-select"
                    value={selectedInvestigationKey}
                    onChange={(event) => setSelectedInvestigationKey(event.target.value)}
                    disabled={
                      mutating ||
                      investigationOptionsLoading ||
                      Boolean(investigationOptionsError)
                    }
                    className="ds-test-select"
                  >
                    <option value="">
                      {investigationOptionsLoading
                        ? "Loading investigations..."
                        : "Select an investigation..."}
                    </option>
                    {groupedInvestigations.map((group) => (
                      <optgroup key={group.category} label={labelize(group.category)}>
                        {group.items.map((investigation) => (
                          <option key={investigation.key} value={investigation.key}>
                            {investigation.label}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  <button
                    className="btn btn-primary ds-test-send-btn"
                    onClick={handleOrderTest}
                    disabled={!canOrderInvestigation}
                    aria-label="Order investigation"
                  >
                    {mutating ? "..." : <Send size={14} />}
                  </button>
                </div>
                {investigationOptionsError && (
                  <p className="ds-test-options-error">{investigationOptionsError}</p>
                )}
              </div>
              <ActivityList activities={activities} />
            </div>
          )}
        </div>
      </div>

      <AssessmentPanel detail={detail} />

      {confirmEnd && (
        <ConfirmEndDialog
          mutating={mutating}
          onCancel={() => setConfirmEnd(false)}
          onConfirm={handleEndCase}
        />
      )}

      {confirmReveal && (
        <ConfirmRevealDialog
          mutating={mutating}
          onCancel={() => setConfirmReveal(false)}
          onConfirm={handleRevealCondition}
        />
      )}
    </div>
  );
}

function TranscriptMessage({ message, patientLabel }) {
  const role = message.role === "doctor" ? "doctor" : message.role === "patient" ? "patient" : "system";
  return (
    <div className={`ds-chat-msg role-${role} ${message.pending ? "is-pending" : ""}`}>
      <div className="ds-msg-header">
        <span className="ds-msg-role-icon">
          {role === "doctor" ? <Stethoscope size={14} /> : <UserRound size={14} />}
        </span>
        <span className="ds-msg-role-label">
          {role === "doctor" ? "You (Doctor)" : patientLabel}
          {message.pending ? " - sending" : ""}
        </span>
      </div>
      <div className="ds-msg-body">
        <ReactMarkdown>{String(message.content || "")}</ReactMarkdown>
      </div>
    </div>
  );
}

function ActivityList({ activities }) {
  const visible = buildInvestigationCards(activities);

  return (
    <div className="ds-test-results">
      {visible.length === 0 ? (
        <div className="ds-test-empty">
          <FlaskConical size={20} />
          <p>No investigations ordered yet.</p>
        </div>
      ) : (
        visible.map((card) => (
          <div key={card.id} className={`ds-test-card ds-test-card-${card.state}`}>
            <div className="ds-test-card-header">
              <span className="ds-test-name">{card.title}</span>
              <div className="ds-test-badges">
                <span className={`ds-test-badge badge-${card.state}`}>
                  {card.badge}
                </span>
              </div>
            </div>
            <div className="ds-test-result-text">{card.body}</div>
          </div>
        ))
      )}
    </div>
  );
}

export function buildInvestigationCards(activities = []) {
  const cards = [];
  const cardIndex = new Map();
  let lastOrderedKey = null;

  activities.forEach((activity, index) => {
    if (!isInvestigationActivity(activity)) return;

    const data = activity.data || {};
    const activityType = activity.activity_type;
    const requestKey = investigationRequestKey(activity, lastOrderedKey, index);
    const existing = cardIndex.get(requestKey);
    const card = existing || {
      id: requestKey,
      orderSequence: activity.sequence ?? index,
      title: investigationTitle(activity),
      body: "Investigation ordered. Awaiting result.",
      state: "ordered",
      badge: "ORDERED",
      finalSequence: -1,
    };

    if (activityType === "investigation_ordered") {
      card.title = investigationTitle(activity);
      card.body = data.request_text
        ? `Ordered: ${data.request_text}`
        : "Investigation ordered. Awaiting result.";
      lastOrderedKey = requestKey;
    } else {
      const finalRank = activity.sequence ?? index;
      if (finalRank >= card.finalSequence) {
        card.title = investigationTitle(activity) || card.title;
        card.body = investigationResultText(activity);
        card.state = investigationState(activityType);
        card.badge = investigationBadge(activityType);
        card.finalSequence = finalRank;
      }
      lastOrderedKey = null;
    }

    if (!existing) {
      cardIndex.set(requestKey, card);
      cards.push(card);
    }
  });

  return cards.sort((left, right) => left.orderSequence - right.orderSequence);
}

function isInvestigationActivity(activity) {
  return [
    "investigation_ordered",
    "investigation_result_returned",
    "investigation_unavailable",
    "investigation_response_failed",
  ].includes(activity?.activity_type);
}

function investigationRequestKey(activity, fallbackKey, index) {
  const data = activity?.data || {};
  if (data.request_id) return `request:${data.request_id}`;
  const parsed = Array.isArray(data.parsed_requests) ? data.parsed_requests[0] : null;
  if (parsed?.request_id) return `request:${parsed.request_id}`;
  if (activity.activity_type !== "investigation_ordered" && fallbackKey) {
    return fallbackKey;
  }
  return `label:${normalizeInvestigationKey(
    data.investigation_key ||
      data.investigation_label ||
      data.request_text ||
      activity.display_text ||
      index
  )}`;
}

function normalizeInvestigationKey(value) {
  return String(value || "investigation")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || "investigation";
}

function investigationTitle(activity) {
  const data = activity?.data || {};
  return (
    data.display?.test_name ||
    data.investigation_label ||
    data.investigation_key ||
    data.request_text ||
    activity?.display_text ||
    "Investigation"
  );
}

function investigationState(activityType) {
  if (activityType === "investigation_result_returned") return "supported";
  if (activityType === "investigation_unavailable") return "unavailable";
  if (activityType === "investigation_response_failed") return "error";
  return "ordered";
}

function investigationBadge(activityType) {
  if (activityType === "investigation_result_returned") return "SUPPORTED";
  if (activityType === "investigation_unavailable") return "UNAVAILABLE";
  if (activityType === "investigation_response_failed") return "ERROR";
  return "ORDERED";
}

function investigationResultText(activity) {
  const data = activity?.data || {};
  if (typeof data.display?.result === "string" && data.display.result.trim()) {
    return data.display.result.trim();
  }
  const result = data.result;
  if (typeof result === "string") return result;
  if (result && typeof result === "object") {
    return result.summary || JSON.stringify(result);
  }
  return (
    data.reason ||
    data.learner_explanation ||
    activity.display_text ||
    "Investigation result returned."
  );
}

function AssessmentPanel({ detail }) {
  if (detail?.state === "abandoned") {
    return (
      <section className="ds-assessment-panel ds-assessment-empty">
        <FileText size={18} />
        <div>
          <h2>No assessment available</h2>
          <p>This case was marked abandoned after inactivity, so no evaluator assessment was generated.</p>
        </div>
      </section>
    );
  }

  if (detail?.state !== "completed") return null;

  if (!detail.assessment) {
    return (
      <section className="ds-assessment-panel ds-assessment-empty">
        <FileText size={18} />
        <div>
          <h2>Assessment pending</h2>
          <p>No evaluator assessment is available yet.</p>
        </div>
      </section>
    );
  }

  const payload = detail.assessment.payload || {};
  const competencies = payload.competency_judgements || {};

  return (
    <section className="ds-assessment-panel">
      <div className="ds-assessment-header">
        <FileText size={18} />
        <div>
          <h2>Evaluator Assessment</h2>
          {detail?.meta?.medical_condition && (
            <p className="ds-assessment-condition">
              <strong>Medical Condition:</strong> {detail.meta.medical_condition}
            </p>
          )}
          <p>{detail.assessment.summary || "Assessment available."}</p>
        </div>
      </div>
      {payload.overall_judgement?.summary && (
        <div className="ds-assessment-callout">
          {payload.overall_judgement.summary}
        </div>
      )}
      <div className="ds-assessment-grid">
        {Object.entries(competencies).map(([key, value]) => (
          <div key={key} className="ds-assessment-card">
            <h3>{labelize(key)}</h3>
            <span>{value?.judgement || "review"}</span>
            <p>{value?.summary || "No summary provided."}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function TerminalNotice({ state, hasAssessment }) {
  if (state === "abandoned") {
    return (
      <div className="ds-terminal-notice">
        This case was marked abandoned after inactivity. It cannot be continued and no evaluator assessment was generated.
      </div>
    );
  }
  if (state === "completed") {
    return (
      <div className="ds-terminal-notice">
        This case is completed and read-only. {hasAssessment ? "Assessment available." : "No assessment available yet."}
      </div>
    );
  }
  return (
    <div className="ds-terminal-notice ds-terminal-error">
      This case ended in an error state and is read-only.
    </div>
  );
}

function InlineError({ error, onRetry }) {
  return (
    <div className="ds-setup-error ds-workspace-error" role="alert">
      <AlertCircle size={14} />
      <div>
        <strong>{error.message}</strong>
        {error.correlationId && <p>Reference: {error.correlationId}</p>}
      </div>
      <button className="btn btn-secondary" onClick={onRetry}>
        Retry
      </button>
    </div>
  );
}

function SessionErrorState({ error, onRetry }) {
  const isNotFound = error.errorCode === "session_not_found" || error.status === 404;
  return (
    <div className="ds-session-error-state">
      <AlertCircle size={24} />
      <h1>{isNotFound ? "Session not found" : "Unable to restore session"}</h1>
      <p>{error.message}</p>
      {error.correlationId && <p className="ds-correlation">Reference: {error.correlationId}</p>}
      <div className="ds-error-actions">
        <button className="btn btn-secondary" onClick={onRetry}>
          Retry
        </button>
      </div>
    </div>
  );
}

function ConfirmEndDialog({ mutating, onCancel, onConfirm }) {
  return (
    <div className="ds-modal-backdrop" role="dialog" aria-modal="true">
      <div className="ds-confirm-dialog">
        <h2>End Case and Generate Assessment?</h2>
        <p>
          This will finish the current case and generate an evaluator assessment.
          You will not be able to continue editing this case after it is completed.
        </p>
        <div className="ds-confirm-actions">
          <button className="btn btn-secondary" onClick={onCancel} disabled={mutating}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={onConfirm} disabled={mutating}>
            {mutating ? "Ending..." : "End Case and Generate Assessment"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmRevealDialog({ mutating, onCancel, onConfirm }) {
  return (
    <div className="ds-modal-backdrop" role="dialog" aria-modal="true">
      <div className="ds-confirm-dialog">
        <h2>Reveal Medical Condition?</h2>
        <p>
          This will reveal the diagnosis for learning purposes. It may reduce
          the realism of the simulation. Continue?
        </p>
        <div className="ds-confirm-actions">
          <button className="btn btn-secondary" onClick={onCancel} disabled={mutating}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={onConfirm} disabled={mutating}>
            {mutating ? "Revealing..." : "Reveal Medical Condition"}
          </button>
        </div>
      </div>
    </div>
  );
}

function labelize(value) {
  return String(value || "")
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function createIdempotencyAction(sessionId, payload) {
  return {
    key: buildModule1IdempotencyKey(),
    sessionId,
    payload,
    inFlight: false,
    retryable: false,
  };
}

function isSamePayloadAction(action, sessionId, payload) {
  return (
    action?.retryable &&
    action.sessionId === sessionId &&
    JSON.stringify(action.payload) === JSON.stringify(payload)
  );
}

function retainOrRetireAction(ref, action, error) {
  if (isAmbiguousNetworkError(error)) {
    action.retryable = true;
  } else if (ref.current === action) {
    ref.current = null;
  }
}

function isAmbiguousNetworkError(error) {
  return error?.errorCode === "network_error" && !error?.status;
}
